import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Datetime from "react-datetime";
import moment from "moment";
import "react-datetime/css/react-datetime.css";
import { useSelector } from "react-redux";
import { CalculationBasis, CashbackConfigV2, PermissionCategory, PlayerCashbackOverride } from "../../http/protocol";
import { ApplicationState } from "../../user/UserStore";
import { getContainer } from "../../ioc/IOCSetup";
import { Collapsible } from "../Collapsible";
import { FeatureFlags, useFeatureFlag } from "../../feature-flags/FeatureFlagContext";
import { t } from "../../i18n";
import { formatExpiry, formatFraction } from "../bonus/utils/cashbackFormat";
import {
  blockedFractionMessage,
  blockingFractionLimit,
  isLargeFraction,
  isNewlyBlockingFraction,
  largeFractionThreshold,
  useCashbackFractionThresholds,
} from "../bonus/cashbackFractionThresholds";
import "./UserCashbackOverridesComponent.scss";

interface Props {
  userId: number;
}

interface EditableOverride extends PlayerCashbackOverride {
  _editing?: boolean;
  _rateText?: string;
  _expiresAt?: number;
  _saving?: boolean;
  _error?: string;
}

// HttpService rejects with a plain { message, status } object, not an Error instance, so reach
// for that message before falling back; a raw object stringifies to the useless "[object Object]".
function errorMessage(err: unknown): string {
  if (err != null && typeof err === "object" && typeof (err as { message?: unknown }).message === "string") {
    return (err as { message: string }).message;
  }
  return err instanceof Error ? err.message : String(err);
}

function parseRate(text: string): number | undefined {
  const value = Number.parseFloat(text);
  if (Number.isNaN(value) || value < 0 || value > 1) {
    return undefined;
  }
  return value;
}

// react-datetime yields a moment when a valid date is picked and a string while the field is
// being typed or cleared; an empty field means a permanent override (no expiry).
function expiryFromPicker(value: moment.Moment | string): number | undefined {
  if (typeof value === "string") {
    return undefined;
  }
  return value.toDate().getTime();
}

export function UserCashbackOverridesComponent({ userId }: Props) {
  const cashbackV2Enabled = useFeatureFlag(FeatureFlags.CASHBACK_V2_ENABLED);
  const hasBonusPermission = useSelector((state: ApplicationState) => state.user.permissions?.[PermissionCategory.BONUS] != null);
  const thresholds = useCashbackFractionThresholds();

  const [overrides, setOverrides] = useState<EditableOverride[]>([]);
  const [configs, setConfigs] = useState<CashbackConfigV2[]>([]);
  const [includeExpired, setIncludeExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [adding, setAdding] = useState(false);
  const [newConfigId, setNewConfigId] = useState<number | undefined>(undefined);
  const [newRateText, setNewRateText] = useState<string>("");
  const [newExpiresAt, setNewExpiresAt] = useState<number | undefined>(undefined);
  const [newError, setNewError] = useState<string | undefined>(undefined);
  const [savingNew, setSavingNew] = useState(false);

  const nameByConfigId = useMemo(() => {
    const map = new Map<number, string>();
    for (const config of configs) {
      if (config.id != null) {
        map.set(config.id, config.name ?? String(config.id));
      }
    }
    return map;
  }, [configs]);

  // An override's fraction is judged against its parent config's reward type and calculation basis,
  // exactly as the standalone override editor and the config editors do; a config not yet loaded (or
  // none selected) falls back to the stricter UNWEIGHTED basis and the cashback (default) set.
  const configFractionContext = useCallback(
    (configId: number | undefined) => {
      const config = configs.find((item) => item.id === configId);
      return { basis: config?.calculationBasis ?? CalculationBasis.UNWEIGHTED, type: config?.type };
    },
    [configs],
  );

  const loadConfigs = useCallback(async () => {
    try {
      const response = await getContainer().getCashbackConfigV2Service().list({ limit: 1000, offset: 0, orderBy: "id" });
      setConfigs(response.items ?? []);
    } catch (err: unknown) {
      console.error("Failed to load cashback configs:", err);
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await getContainer().getPlayerCashbackOverrideService().listPaged({ userId, includeExpired, limit: 1000, offset: 0 });
      setOverrides((response.items ?? []).map((item) => ({ ...item })));
    } catch (err: unknown) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [userId, includeExpired]);

  useEffect(() => {
    if (cashbackV2Enabled && hasBonusPermission) {
      reload();
      loadConfigs();
    }
  }, [cashbackV2Enabled, hasBonusPermission, reload, loadConfigs]);

  const beginEdit = useCallback((idx: number) => {
    setOverrides((prev) => {
      const next = [...prev];
      const row = next[idx];
      next[idx] = {
        ...row,
        _editing: true,
        _rateText: formatFraction(row.fraction),
        _expiresAt: row.expiresAt as unknown as number | undefined,
        _error: undefined,
      };
      return next;
    });
  }, []);

  const cancelEdit = useCallback((idx: number) => {
    setOverrides((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], _editing: false, _error: undefined };
      return next;
    });
  }, []);

  const saveEdit = useCallback(
    async (idx: number) => {
      const row = overrides[idx];
      const fraction = parseRate(row._rateText ?? "");
      if (fraction == null) {
        setOverrides((prev) => {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            _error: t("cashback.player-overrides.fraction-out-of-range", undefined, "Rate must be a factor between 0 and 1, e.g. 0.15."),
          };
          return next;
        });
        return;
      }
      if (row._expiresAt != null && row._expiresAt < Date.now()) {
        setOverrides((prev) => {
          const next = [...prev];
          next[idx] = { ...next[idx], _error: t("cashback.player-overrides.expires-in-past", undefined, "Expiry date cannot be in the past.") };
          return next;
        });
        return;
      }
      // The ceiling blocks only a fraction changed above it this session; the loaded value is the
      // grandfather baseline, so an untouched over-ceiling override still saves.
      const { type } = configFractionContext(row.configId);
      if (isNewlyBlockingFraction(row._rateText, formatFraction(row.fraction), thresholds, type)) {
        const message = blockedFractionMessage("cashback.config-v2.large-fraction-blocked", blockingFractionLimit(thresholds, type));
        setOverrides((prev) => {
          const next = [...prev];
          next[idx] = { ...next[idx], _error: message };
          return next;
        });
        return;
      }
      setOverrides((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], _saving: true, _error: undefined };
        return next;
      });
      try {
        await getContainer()
          .getPlayerCashbackOverrideService()
          .save({ userId, configId: row.configId, fraction, expiresAt: row._expiresAt as unknown as Date | undefined });
        await reload();
      } catch (err: unknown) {
        setOverrides((prev) => {
          const next = [...prev];
          if (next[idx]) {
            next[idx] = { ...next[idx], _saving: false, _error: errorMessage(err) };
          }
          return next;
        });
      }
    },
    [overrides, userId, reload, thresholds, configFractionContext],
  );

  const remove = useCallback(
    async (idx: number) => {
      const row = overrides[idx];
      if (row.userId == null || row.configId == null) {
        return;
      }
      if (!window.confirm(t("confirm.delete", undefined, "Are you sure?"))) {
        return;
      }
      try {
        await getContainer().getPlayerCashbackOverrideService().removeOverride(row.userId, row.configId);
        await reload();
      } catch (err: unknown) {
        setOverrides((prev) => {
          const next = [...prev];
          if (next[idx]) {
            next[idx] = { ...next[idx], _error: errorMessage(err) };
          }
          return next;
        });
      }
    },
    [overrides, reload],
  );

  const resetAddForm = useCallback(() => {
    setAdding(false);
    setNewConfigId(undefined);
    setNewRateText("");
    setNewExpiresAt(undefined);
    setNewError(undefined);
  }, []);

  const saveNew = useCallback(async () => {
    if (newConfigId == null) {
      setNewError(t("cashback.player-overrides.config-required", undefined, "Please select a config."));
      return;
    }
    const fraction = parseRate(newRateText);
    if (fraction == null) {
      setNewError(t("cashback.player-overrides.fraction-out-of-range", undefined, "Rate must be a factor between 0 and 1, e.g. 0.15."));
      return;
    }
    if (newExpiresAt != null && newExpiresAt < Date.now()) {
      setNewError(t("cashback.player-overrides.expires-in-past", undefined, "Expiry date cannot be in the past."));
      return;
    }
    // A new override has no baseline, so any over-ceiling fraction is blocked outright.
    const { type } = configFractionContext(newConfigId);
    if (isNewlyBlockingFraction(newRateText, undefined, thresholds, type)) {
      setNewError(blockedFractionMessage("cashback.config-v2.large-fraction-blocked", blockingFractionLimit(thresholds, type)));
      return;
    }
    setSavingNew(true);
    setNewError(undefined);
    try {
      await getContainer()
        .getPlayerCashbackOverrideService()
        .save({ userId, configId: newConfigId, fraction, expiresAt: newExpiresAt as unknown as Date | undefined });
      resetAddForm();
      await reload();
    } catch (err: unknown) {
      setNewError(errorMessage(err));
    } finally {
      setSavingNew(false);
    }
  }, [userId, newConfigId, newRateText, newExpiresAt, reload, resetAddForm, thresholds, configFractionContext]);

  if (!cashbackV2Enabled || !hasBonusPermission) {
    return null;
  }

  // Advisory-only banner mirroring the config editors: shown while the entered fraction is unusually
  // large for the parent config's reward type and basis, but never prevents saving.
  const renderLargeFractionWarning = (configId: number | undefined, text: string, testId: string): JSX.Element | null => {
    if (!thresholds) {
      return null;
    }
    const { basis, type } = configFractionContext(configId);
    if (!isLargeFraction(Number(text), basis, thresholds, type)) {
      return null;
    }
    return (
      <tr>
        <td
          colSpan={4}
          className="text-warning uco-warning"
          data-testid={testId}
        >
          <strong>{t("cashback.config-v2.large-fraction-warning-title", undefined, "Unusually large fraction")}</strong>{" "}
          {t(
            "cashback.config-v2.large-fraction-warning",
            { threshold: largeFractionThreshold(basis, thresholds, type), basis },
            "A fraction of {{threshold}} or more is unusually large for {{basis}} and may pay out far more than intended. This is advisory only — you can still save.",
          )}
        </td>
      </tr>
    );
  };

  return (
    <Collapsible
      title={t("cashback.player-overrides.card-title", undefined, "Cashback Overrides")}
      collapsed={true}
    >
      <div className="container-fluid">
        <div className="row mb-2">
          <div className="col-sm-12 d-flex justify-content-between align-items-center">
            <label className="mb-0">
              <input
                type="checkbox"
                className="mr-2"
                checked={includeExpired}
                onChange={(e) => setIncludeExpired(e.target.checked)}
                data-testid="user-cashback-override-include-expired"
              />
              {t("cashback.player-overrides.show-expired", undefined, "Show expired")}
            </label>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setAdding(true)}
              disabled={adding}
              data-testid="user-cashback-override-add"
            >
              {t("cashback.player-overrides.create", undefined, "Add Override")}
            </button>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <table className="table table-striped uco-table">
          <thead>
            <tr>
              <th>{t("cashback.player-overrides.config-id", undefined, "Config")}</th>
              <th>{t("cashback.player-overrides.rate", undefined, "Rate")}</th>
              <th>{t("cashback.player-overrides.expires", undefined, "Expires")}</th>
              <th>{t("cashback.player-overrides.actions", undefined, "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {adding && (
              <tr
                className="uco-edit-row"
                data-testid="user-cashback-override-add-row"
              >
                <td>
                  <select
                    className="form-control uco-config-select"
                    value={newConfigId ?? ""}
                    onChange={(e) => setNewConfigId(e.target.value === "" ? undefined : Number(e.target.value))}
                    data-testid="user-cashback-override-new-config"
                  >
                    <option value="">{t("cashback.player-overrides.all-configs", undefined, "All configs")}</option>
                    {configs.map((config) => (
                      <option
                        key={config.id}
                        value={config.id}
                      >
                        {config.name ?? String(config.id)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-control uco-rate-input"
                    value={newRateText}
                    onChange={(e) => setNewRateText(e.target.value.replace(",", "."))}
                    placeholder={t("cashback.player-overrides.rate-placeholder", undefined, "e.g. 0.1")}
                    data-testid="user-cashback-override-new-rate"
                  />
                </td>
                <td>
                  <div className="uco-expires">
                    <Datetime
                      timeFormat="HH:mm"
                      dateFormat="YYYY-MM-DD"
                      value={newExpiresAt != null ? new Date(newExpiresAt) : ""}
                      onChange={(value) => setNewExpiresAt(expiryFromPicker(value))}
                      inputProps={
                        {
                          "data-testid": "user-cashback-override-new-expires",
                          placeholder: t("cashback.player-overrides.no-expiry", undefined, "Permanent"),
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  </div>
                </td>
                <td>
                  <div className="uco-actions">
                    <button
                      className="btn btn-success btn-sm"
                      onClick={saveNew}
                      disabled={savingNew}
                      title={t("cashback.player-overrides.save", undefined, "Save")}
                      aria-label={t("cashback.player-overrides.save", undefined, "Save")}
                      data-testid="user-cashback-override-new-save"
                    >
                      <i className="fa fa-check" />
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={resetAddForm}
                      disabled={savingNew}
                      title={t("button.cancel", undefined, "Cancel")}
                      aria-label={t("button.cancel", undefined, "Cancel")}
                      data-testid="user-cashback-override-new-cancel"
                    >
                      <i className="fa fa-times" />
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {adding && renderLargeFractionWarning(newConfigId, newRateText, "user-cashback-override-new-large-fraction-warning")}
            {adding && newError && (
              <tr>
                <td
                  colSpan={4}
                  className="text-danger uco-error"
                >
                  {newError}
                </td>
              </tr>
            )}

            {overrides.map((item, idx) => (
              <React.Fragment key={`${item.userId}-${item.configId}`}>
                <tr className={item._editing ? "uco-edit-row" : undefined}>
                  <td>
                    {item.configId != null ? <Link to={"/cashback/v2/config/" + item.configId}>{nameByConfigId.get(item.configId) ?? item.configId}</Link> : ""}
                  </td>
                  <td>
                    {item._editing ? (
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control uco-rate-input"
                        value={item._rateText ?? ""}
                        placeholder={t("cashback.player-overrides.rate-placeholder", undefined, "e.g. 0.1")}
                        onChange={(e) => {
                          const text = e.target.value.replace(",", ".");
                          setOverrides((prev) => {
                            const next = [...prev];
                            next[idx] = { ...next[idx], _rateText: text };
                            return next;
                          });
                        }}
                        data-testid="user-cashback-override-edit-rate"
                      />
                    ) : (
                      formatFraction(item.fraction)
                    )}
                  </td>
                  <td>
                    {item._editing ? (
                      <div className="uco-expires">
                        <Datetime
                          timeFormat="HH:mm"
                          dateFormat="YYYY-MM-DD"
                          value={item._expiresAt != null ? new Date(item._expiresAt) : ""}
                          onChange={(value) => {
                            const millis = expiryFromPicker(value);
                            setOverrides((prev) => {
                              const next = [...prev];
                              next[idx] = { ...next[idx], _expiresAt: millis };
                              return next;
                            });
                          }}
                          inputProps={
                            {
                              "data-testid": "user-cashback-override-edit-expires",
                              placeholder: t("cashback.player-overrides.no-expiry", undefined, "Permanent"),
                            } as React.InputHTMLAttributes<HTMLInputElement>
                          }
                        />
                      </div>
                    ) : (
                      formatExpiry(item.expiresAt as unknown as number | undefined, t("cashback.player-overrides.no-expiry", undefined, "Permanent"))
                    )}
                  </td>
                  <td>
                    {item._editing ? (
                      <div className="uco-actions">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => saveEdit(idx)}
                          disabled={item._saving}
                          title={t("cashback.player-overrides.save", undefined, "Save")}
                          aria-label={t("cashback.player-overrides.save", undefined, "Save")}
                          data-testid="user-cashback-override-edit-save"
                        >
                          <i className="fa fa-check" />
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => cancelEdit(idx)}
                          disabled={item._saving}
                          title={t("button.cancel", undefined, "Cancel")}
                          aria-label={t("button.cancel", undefined, "Cancel")}
                          data-testid="user-cashback-override-edit-cancel"
                        >
                          <i className="fa fa-times" />
                        </button>
                      </div>
                    ) : (
                      <div className="uco-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => beginEdit(idx)}
                          title={t("cashback.player-overrides.edit", undefined, "Edit")}
                          aria-label={t("cashback.player-overrides.edit", undefined, "Edit")}
                          data-testid="user-cashback-override-edit"
                        >
                          <i className="fa fa-edit" />
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => remove(idx)}
                          title={t("cashback.player-overrides.remove", undefined, "Remove")}
                          aria-label={t("cashback.player-overrides.remove", undefined, "Remove")}
                          data-testid="user-cashback-override-remove"
                        >
                          <i className="fa fa-trash" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                {item._editing && renderLargeFractionWarning(item.configId, item._rateText ?? "", "user-cashback-override-edit-large-fraction-warning")}
                {item._error && (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-danger uco-error"
                    >
                      {item._error}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!loading && !adding && overrides.length === 0 && (
              <tr>
                <td colSpan={4}>{t("cashback.player-overrides.none", undefined, "No cashback overrides for this player.")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Collapsible>
  );
}
